import { jwtDecode } from 'jwt-decode';
import { UserProfile } from '@/contexts/AuthContext';
import { useAuth } from '@/contexts/AuthContext';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { cn } from '@/utils/utils';
import { Eye, Pencil, Layers } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { VscMarkdown, VscChromeClose } from 'react-icons/vsc';
import { useEffect, useState } from 'react';
import UserProfileButton from '../shared/UserProfileButton';

// Define the Page interface for tabs
interface Page {
  index: number;
  name: string;
  route: string;
  visible: boolean;
  mode: string;
}

interface SpaceHeaderProps {
  selectedIndex: number;
  visiblePages: Page[];
  onTabSelect: (index: number) => void;
  onTabClose: (index: number, e: React.MouseEvent) => void;
  onAddTab: () => void;
  onModeToggle: (index: number) => void;
}

// Add proper types for token expiration check
interface TokenData {
  exp: number;
  email: string;
  name: string;
  picture: string;
}

function isTokenExpired(): boolean {
  const token = localStorage.getItem('google_access_token');
  if (!token) return true;
  
  try {
    const decoded = jwtDecode<TokenData>(token);
    // Add 5 minute buffer before actual expiration
    return Date.now() >= ((decoded.exp * 1000) - 300000); // 5 minutes in milliseconds
  } catch (error) {
    console.error('Error checking token expiration:', error);
    return true;
  }
}

export default function SpaceHeader({ selectedIndex, visiblePages, onTabSelect, onTabClose, onAddTab, onModeToggle }: SpaceHeaderProps) {
  const { userProfile, handleLogout, isAuthenticated, initiateGoogleLogin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  
  // Check for mobile screen size
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Debug user profile data
  useEffect(() => {
    if (userProfile) {
      console.log('SpaceHeader userProfile:', userProfile);
    }
  }, [userProfile]);

  // Render tab button
  const renderTabButton = (page: Page) => {
    const isSelected = selectedIndex === page.index;
    
    return (
      <div
        key={page.index}
        className={cn(
          "flex items-center h-full px-4 border-r border-gray-200 dark:border-gray-700 cursor-pointer",
          isSelected 
            ? "bg-white dark:bg-gray-800 text-purple-600 dark:text-purple-400 border-b-2 border-purple-500" 
            : "bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 border-b-2 border-transparent hover:border-gray-300"
        )}
        onClick={() => onTabSelect(page.index)}
      >
        <VscMarkdown className="mr-2 h-4 w-4 text-purple-500" />
        <span className="text-sm">{page.name}</span>
        
        {/* Mode toggle icons */}
        <div className="ml-2 flex items-center">
          {page.mode === 'view' ? (
            <button
              className={cn(
                "p-1 rounded-full",
                isSelected 
                  ? "hover:bg-gray-200 dark:hover:bg-gray-700 text-blue-500" 
                  : "hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-500"
              )}
              onClick={(e) => {
                e.stopPropagation();
                onModeToggle(page.index);
              }}
              title="Switch to edit mode"
            >
              <Eye className="h-3.5 w-3.5" />
            </button>
          ) : (
            <button
              className={cn(
                "p-1 rounded-full",
                isSelected 
                  ? "hover:bg-gray-200 dark:hover:bg-gray-700 text-green-500" 
                  : "hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-500"
              )}
              onClick={(e) => {
                e.stopPropagation();
                onModeToggle(page.index);
              }}
              title="Switch to view mode"
            >
              <Pencil className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
        
        <button
          className={cn(
            "ml-2 p-1 rounded-full",
            isSelected 
              ? "hover:bg-gray-200 dark:hover:bg-gray-700" 
              : "hover:bg-gray-300 dark:hover:bg-gray-600"
          )}
          onClick={(e) => onTabClose(page.index, e)}
        >
          <VscChromeClose className="h-3 w-3" />
        </button>
      </div>
    );
  };

  return (
    <header className="border-b">
      <div className="flex items-center px-4 h-11">
        {/* Space Tab */}
        <div className="flex h-full">
          <Button
            variant="ghost"
            className={cn(
              "flex items-center gap-2 px-4 py-2 rounded-none border-b-2 h-full",
              "border-blue-500 text-blue-500"
            )}
          >
            <Layers className="h-4 w-4" />
            <span>Space</span>
          </Button>
          
          {/* Tab Buttons */}
          {visiblePages.map(page => renderTabButton(page))}
          
          {/* Add Tab Button */}
          <button
            className="flex items-center justify-center h-full w-9 text-gray-500 hover:bg-gray-200 dark:hover:bg-gray-600 hover:text-purple-500"
            onClick={onAddTab}
          >
            <span className="text-lg">+</span>
          </button>
        </div>

        {/* User Profile / Auth Section - Only shown on mobile */}
        {isMobile && (
          <div className="ml-auto flex items-center">
            <UserProfileButton 
              userProfile={userProfile || null}
              handleLogout={handleLogout}
              initiateGoogleLogin={initiateGoogleLogin}
              buttonStyle="circle"
            />
          </div>
        )}
      </div>
    </header>
  );
}